import { useEffect, useState } from 'react'
import { Download, File as FileIcon } from 'lucide-react'
import { fetchStewardFileBlob } from '@/lib/api'
import { Markdown } from './Markdown'

interface Props {
  path: string
  name: string
  ext: string
}

const IMAGE = new Set(['png', 'jpg', 'jpeg', 'gif', 'webp', 'svg', 'avif'])
const TEXT = new Set(['md', 'txt', 'csv'])

/**
 * A file opened as a card in the chat. Fetches the file WITH auth (object URL), then
 * renders by type: images + PDFs inline, markdown/text rendered, everything else
 * (docx/pptx/…) as an open/download card. Object URLs are revoked on unmount.
 * Email links aren't files — they open in Mail straight from the brief's markdown.
 */
export function FileCard({ path, name, ext }: Props) {
  const [url, setUrl] = useState<string | null>(null)
  const [text, setText] = useState<string | null>(null)
  const [error, setError] = useState<string | null>(null)
  const kind = IMAGE.has(ext) ? 'image' : ext === 'pdf' ? 'pdf' : TEXT.has(ext) ? 'text' : 'other'

  useEffect(() => {
    let alive = true
    let objectUrl: string | null = null
    fetchStewardFileBlob(path)
      .then(async (blob) => {
        if (!alive) return
        if (kind === 'text') {
          setText(await blob.text())
        } else {
          objectUrl = URL.createObjectURL(blob)
          setUrl(objectUrl)
        }
      })
      .catch((e) => alive && setError(e instanceof Error ? e.message : 'Failed to load file'))
    return () => {
      alive = false
      if (objectUrl) URL.revokeObjectURL(objectUrl)
    }
  }, [path, kind])

  return (
    <div className="max-w-[85%] overflow-hidden rounded-2xl border border-border bg-muted/20 text-[15px] text-foreground">
      <div className="flex items-center gap-2 border-b border-border px-3 py-2 text-[12px] font-light text-muted-foreground">
        <FileIcon className="h-3.5 w-3.5 shrink-0" />
        <span className="min-w-0 flex-1 truncate text-foreground">{name}</span>
        {url && (
          <a href={url} download={name} target="_blank" rel="noreferrer"
             className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 hover:bg-muted" title="Open / download">
            <Download className="h-3.5 w-3.5" />
          </a>
        )}
      </div>

      <div className="p-3">
        {error ? (
          <div className="text-[13px] text-red-500/90">Couldn't open this file: {error}</div>
        ) : kind === 'image' ? (
          url ? <img src={url} alt={name} className="max-h-[480px] w-auto rounded" /> : <Loading />
        ) : kind === 'pdf' ? (
          url ? <iframe src={url} title={name} className="h-[480px] w-full rounded border-0" /> : <Loading />
        ) : kind === 'text' ? (
          text !== null ? (
            ext === 'md' ? (
              <Markdown source={text} className="steward-prose" />
            ) : (
              <pre className="overflow-x-auto whitespace-pre-wrap text-[13px] leading-relaxed">{text}</pre>
            )
          ) : (
            <Loading />
          )
        ) : (
          // docx / pptx / xlsx / etc — open/download (inline preview is a later add).
          <div className="text-[13px] font-light text-muted-foreground">
            {url ? (
              <a href={url} download={name} target="_blank" rel="noreferrer" className="text-foreground underline">
                Open {name}
              </a>
            ) : (
              <Loading />
            )}
          </div>
        )}
      </div>
    </div>
  )
}

function Loading() {
  return <div className="text-[13px] font-light text-muted-foreground">Loading…</div>
}
